import * as React from "react";
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { SubNavigation, IMenuItem } from "../SubNavigation";
import HomeIcon from '@material-ui/icons/Home';
import { GirIcon } from '../../atoms/icons/GirIcon';
import { Dispatch } from "redux";
import { RootActionCreators, IApplicationState } from "../../../store";
import { useTranslation } from 'react-i18next';
import { UserAccessLevel } from "../../../store/user/userInfo";
import { connect } from "react-redux";
import { FetchState } from "../../../entities/FetchState";

// CSS
const useStyles = makeStyles((theme) => ({ }));

export enum SettingsNavigationPage {
    Intro = "Intro",
    VaphToken = "VaphToken",
}

interface ICoolMenuItem extends IMenuItem {
    page: SettingsNavigationPage;
    requiredAccessLevel?: UserAccessLevel;
}

// All the incoming values
interface IProps {
    selected?: SettingsNavigationPage;
    children?: React.ReactNode;
}

// Props from State
interface IPropsFromState {
    userAccessLevels: UserAccessLevel[];
}
const mapStateToProps = (state: IApplicationState): IPropsFromState => {
    // Get user access levels from store, or empty array if not loaded
    if (state.user.userInfo.UserInfo.state === FetchState.Success) {
        return {
            userAccessLevels: state.user.userInfo.UserInfo.data.permissions
        };
    }
    return {
        userAccessLevels: []
    };
};

// All Navigation Functions
export interface INavigations {
    toIntro: () => void;
    toVaphToken: () => void;
}

// Props from Dispatch
interface IPropsFromDispatch {
    navigations: INavigations;
    loadUserInfo: () => void;
}
const mapDispatchToProps = (dispatch: Dispatch): IPropsFromDispatch => ({
    navigations: {
        toIntro: () => dispatch(RootActionCreators.Navigate.ToUrl('/database/settings/')),
        toVaphToken: () => dispatch(RootActionCreators.Navigate.ToUrl('/database/settings/vaphtoken')),
    },
    loadUserInfo: () => dispatch(RootActionCreators.User.userInfo.FetchData()),
});

// Actual Component
export type AllProps = IProps & IPropsFromState & IPropsFromDispatch;
export const SettingsNavigationComponent = (props: AllProps) => {
    const classes = useStyles(useTheme());
    const { t: tCommon } = useTranslation('common');
    const { t: tCompanies } = useTranslation('companies');

    // Load user info if not already loaded
    React.useEffect(() => {
        if (props.userAccessLevels.length === 0) {
            props.loadUserInfo();
        }
    }, [props.userAccessLevels.length, props.loadUserInfo]);

    const allPages: ICoolMenuItem[] = [
        {
            label: tCompanies('title'),
            icon: <HomeIcon />,
            page: SettingsNavigationPage.Intro,
            onClick: props.navigations.toIntro
        },
        {
            label: tCompanies('vaphToken'),
            icon: <GirIcon />,
            page: SettingsNavigationPage.VaphToken,
            onClick: props.navigations.toVaphToken,
            requiredAccessLevel: UserAccessLevel.VaphTokenManagement
        },
    ];

    // Filter pages based on user access levels
    const pages = allPages.filter(page => {
        // If no access level is required, always show the page
        if (!page.requiredAccessLevel) return true;
        // Check if user has the required access level
        return props.userAccessLevels.includes(page.requiredAccessLevel);
    });

    const selectedIndex = props.selected && pages.findIndex(item => item.page === props.selected);
    if (selectedIndex === -1) {
        // If selected page is not found (might be filtered out), default to first page
        const firstPageIndex = 0;
        return (<SubNavigation menuItems={pages} selectedIndex={firstPageIndex}>{props.children}</SubNavigation>);
    }

    return (<SubNavigation menuItems={pages} selectedIndex={selectedIndex}>{props.children}</SubNavigation>);
}

// Export the connected component
export const SettingsNavigation = connect<IPropsFromState, IPropsFromDispatch, IProps, IApplicationState>(
    mapStateToProps, 
    mapDispatchToProps
)(SettingsNavigationComponent);

export default SettingsNavigation;

